import { withErrorBoundary } from "@sentry/react";
import moment from "moment";
import { useContext } from "react";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Error from "@components/error/error";
import AppLink from "@components/link/link";
import { classNames } from "@helpers/css";
import { getUrl, removeCurrentYear } from "@helpers/helpers";

import DetectedOneononeBanner from "./detected-oneonone-banner";
import MeetingPageHeader from "./header";
import { MeetingPageContext } from "./meeting-page-context";
import { MeetingVideoConferenceImage } from "./meeting/video-conference-button";
import { getMeetingVideoConferenceName } from "./meeting/video-conference-helpers";
import PreviousMeetingsPopoverPanel from "./previous-meetings-popover-panel";
import PublishMeetingButton from "./publish-meeting-button";
import MeetingConvertToReview1on1BannerWithSentry from "./review/convert-to-review-1-on-1-banner";

const MeetingDetails = ({
  meeting,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
}) => {
  const { isInExtension, oneononeSubjectId, meetingSidebarExpanded } =
    useContext(MeetingPageContext);
  const meetingGroup = meeting.meetingGroup;
  // Header prev/next arrows + calendar are hidden on drafts (nothing to move
  // between). Otherwise show them for a recurring series, or on the profile
  // 1-on-1 tab where they page through the subject's 1-on-1s across every
  // MeetingGroup (even a non-recurring / imported one-off).
  const showMeetingNavigation =
    !meeting.draft &&
    (meeting.isRecurring ||
      (oneononeSubjectId !== null && oneononeSubjectId !== undefined));

  if (!meetingGroup) return null;

  const meetingDate = meeting.startDatetime
    ? removeCurrentYear(moment(meeting.startDatetime).format("ddd, ll"))
    : "Unscheduled";

  const meetingTitle = meeting.title || meetingGroup.title || "";

  return (
    <>
      <DetectedOneononeBanner meeting={meeting} />

      {/* Stick the meeting header (date, title, action buttons) to the top of
          the scroll container so it stays visible while reading topics. The
          containing block is the per-meeting wrapper (e.g. OneononeRow on the
          1-on-1 tab), so the sticky header scrolls away with its meeting and
          the next meeting's header takes over. The negative top values cancel
          out Layout.Main's top padding so the header pins flush against
          Layout.Header (leaving only this container's py-4 as the gap above the
          date). Main's top padding steps up from py-6 to py-10 at a breakpoint
          that depends on the Ask AI sidebar: `md:` when collapsed, `xl:` when
          expanded (see Layout.Main). The negative top must track the SAME
          breakpoint — otherwise, between md and xl with the sidebar expanded,
          -top-10 would over-cancel the still-24px padding and swallow the gap. */}
      <div
        className={classNames(
          // @container so the title / button below can shorten based on the
          // meeting notes width (e.g. when the Ask AI sidebar is open), not the
          // viewport.
          "@container flex gap-4 items-start justify-between mb-6 py-4",
          !isInExtension && "sticky -top-6 z-10 bg-[#FCFCFD]",
          !isInExtension &&
            (meetingSidebarExpanded ? "xl:-top-10" : "md:-top-10"),
        )}
      >
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1.5">
            <AppLink
              to={
                // On the profile 1-on-1 tab, keep the header date on THIS
                // subject's tab. Linking to the standalone /meeting/:id page
                // would redirect formal 1-on-1s to the global Meeting.subject's
                // profile (the first non-facilitator participant), which for a
                // facilitator-less 1-on-1 is the wrong person — see
                // profile-oneonone-tab.tsx.
                oneononeSubjectId !== null && oneononeSubjectId !== undefined
                  ? `/profile/${oneononeSubjectId}/oneonone/${meeting.id}`
                  : getUrl({
                      meetingGroupId: meeting.meetingGroupId,
                      meetingId: meeting.id,
                    })
              }
              className="tf-header-xl shrink-0 no-underline hover:no-underline"
            >
              {/* Draft meetings (scheduled or not) read as "Unscheduled
                  Meeting" until they're scheduled/published — shortened to just
                  "Unscheduled" when the notes area is narrow. */}
              {meeting.draft ? (
                <>
                  <span className="@xl:hidden">Unscheduled</span>
                  <span className="hidden @xl:inline">Unscheduled Meeting</span>
                </>
              ) : (
                meetingDate
              )}
            </AppLink>
            {showMeetingNavigation && (
              <PreviousMeetingsPopoverPanel meeting={meeting} hideDateButton />
            )}
          </div>
          {meetingTitle && (
            <div className="tf-body-sm-light truncate">{meetingTitle}</div>
          )}
        </div>
        <div className="flex gap-1.5 items-center">
          {!isInExtension && meeting.videoConferenceUrl && (
            <Button
              className="shrink-0 px-0 w-[24px]"
              tooltip={`Join ${getMeetingVideoConferenceName({
                meeting,
                compact: false,
              })}`}
              url={meeting.videoConferenceUrl}
              target="_blank"
              size={ButtonSize.medium}
              theme={buttonTheme.text}
            >
              <MeetingVideoConferenceImage
                meeting={meeting}
                className="h-5 w-5"
              />
            </Button>
          )}
          {!isInExtension && (
            <MeetingPageHeader meetingGroup={meetingGroup} meeting={meeting} />
          )}
          {meeting.draft && (
            <PublishMeetingButton
              meetingId={meeting.id}
              meetingGroupId={meeting.meetingGroupId}
              buttonProps={{
                size: ButtonSize.medium,
                theme: buttonTheme.primary,
              }}
            >
              <span className="@xl:hidden">Schedule</span>
              <span className="hidden @xl:inline">Schedule Meeting</span>
            </PublishMeetingButton>
          )}
        </div>
      </div>

      <div
        className={classNames(
          "flex flex-col gap-4 empty:hidden text-sm text-gray-900",
          !isInExtension && "pb-2",
        )}
      >
        {meeting.meetingGroup.isFormalOneonone &&
          meeting.canUpdate?.permission &&
          meeting.assessmentDeliveriesMatching1on1Template.edges.length ===
            0 && (
            <MeetingConvertToReview1on1BannerWithSentry meeting={meeting} />
          )}
      </div>
    </>
  );
};

const MeetingDetailsWithSentry = withErrorBoundary(MeetingDetails, {
  fallback: <Error description={"The meeting could not be rendered."} />,
});

export default MeetingDetailsWithSentry;
